import { BalanceData } from "../data/balance";
import { TrialModifierData } from "../data/events";
import { Fnv1a64 } from "../maths/fnv1a64";
import { LevelModifiers } from "../sim/modifiers";
import { trialModifiers } from "./trials";

/**
 * Farm twists (MP-25, owner decision D6: optional per run, +25 % rewards). A replay of a cleared level may take one of
 * the Weekly Trial's modifiers, the same for every player on that level that UTC day (a hash of the day and the level
 * picks it from modes.json `farm.modifiers`), and a won twisted run's battle chest pays `rewardBonus` more gold. The
 * twist is applied the way the Trial applies its modifier (trialModifiers: the elites replace the level's, the health
 * multiplier on every raider) and travels in the run's modifiers with a `farm` block, so it is part of the replay record
 * and the state hash, and a twisted run replays, suspends and retries like any other.
 */

/** Seed tag of the day's pick, apart from every other hash of a day. */
const FARM_TAG = "farmTwist";

/** Whether twists are offered at all to a player whose highest cleared level is `highestCleared`. */
export function farmTwistsOpen(balance: BalanceData, highestCleared: number): boolean {
  const f = balance.modes.farm;
  return f.modifiers.length > 0 && highestCleared >= f.unlockAfterLevel;
}

/**
 * The twist a replay of `level` may take on UTC day `day`, or null: twists not open yet, the level not cleared (a first
 * clear is never twisted), below `minLevel`, or no eligible modifier in this bundle's Trial.
 */
export function farmTwistFor(balance: BalanceData, level: number, day: number, highestCleared: number): TrialModifierData | null {
  const f = balance.modes.farm;
  if (!farmTwistsOpen(balance, highestCleared) || level > highestCleared || level < f.minLevel) return null;
  const pool = f.modifiers.map((id) => balance.events.trial.modifiers.find((m) => m.id === id)).filter((m): m is TrialModifierData => m !== undefined);
  if (pool.length === 0) return null;
  const h = new Fnv1a64();
  h.writeString(FARM_TAG);
  h.writeInt(day);
  h.writeInt(level);
  return pool[Number(h.value % BigInt(pool.length))];
}

/** A twisted run's modifiers: the Trial modifier as the Trial plays it, and the farm block (its id and the gold bonus). */
export function farmTwistModifiers(balance: BalanceData, m: TrialModifierData): LevelModifiers {
  return { ...trialModifiers(m), farm: { modifier: m.id, bonus: balance.modes.farm.rewardBonus } };
}

/** The bonus as a whole percent (25 for "0.25"), for the card's "+25 %". */
export function farmBonusPercent(balance: BalanceData): number {
  const PERCENT = 100;
  return balance.modes.farm.rewardBonus.mulInt(PERCENT).floorToInt();
}
